import { unionBounds } from '@/utils/bounds-kit';
import type { Bounds } from '@/version-types';
import { MARKER_GAP_EM } from '@/version-types/constants';
import type { TextRenderInput, TextRenderListMarkerStyle, TextRenderSnapshot } from '../../types';
import { transformBounds } from '../../utils';
import type { ListMarkerShaping, ShapedListMarker } from './shape';

type DrawableMarkerPath = Readonly<{
    d: string;
    transform: string;
}>;

/**
 * 与公开 snapshot 同轮生成、供列表 marker 边界与绘制共同消费的最终几何。
 */
export type ListMarkerGeometry =
    | {
          type: 'character';
          items: {
              paths: DrawableMarkerPath[];
              rotation: string | null;
          }[];
          bounds: Bounds | null;
      }
    | {
          type: 'graphic';
          width: number;
          height: number;
          items: {
              position: { x: number; y: number };
              numberPaths: DrawableMarkerPath[];
          }[];
          bounds: Bounds | null;
      };

type MarkerRotation = Readonly<{ x: number; y: number }>;

/**
 * 需要在缩进末端右对齐的有序字符 marker 样式。
 */
const ORDERED_MARKER_STYLES = new Set<TextRenderListMarkerStyle>(['decimal', 'lower-latin', 'upper-roman']);

/**
 * 竖排时需要旋转 90 度的字符 marker 样式白名单。
 */
const VERTICAL_ROTATED_MARKER_STYLES = new Set<TextRenderListMarkerStyle>(['decimal', 'lower-latin']);

/**
 * 聚合 marker path 原始轮廓的 Y 轴范围。
 *
 * @param paths - 已由文字引擎塑形的 marker paths。
 * @returns 至少一个有效 path 时的最小与最大 Y。
 */
const collectMarkerYBounds = (paths: ShapedListMarker['paths']): { minY: number; maxY: number } | null => {
    if (paths.length === 0) return null;
    return paths.reduce<{ minY: number; maxY: number } | null>((bounds, path) => {
        const minY = path.bounds.top;
        const maxY = path.bounds.bottom;
        return bounds ? { minY: Math.min(bounds.minY, minY), maxY: Math.max(bounds.maxY, maxY) } : { minY, maxY };
    }, null);
};

/**
 * 计算 path 应用最终平移、Y 翻转和可选 90 度旋转后的可见边界。
 *
 * @param path - Text Engine 产生的 marker path。
 * @param translateX - path 在 SVG 用户空间的 X 平移。
 * @param translateY - path 在 SVG 用户空间的 Y 平移。
 * @param rotation - 竖排 marker 的 90 度旋转中心；无需旋转时为 null。
 * @returns 与最终 SVG transform 一致的 path 可见边界。
 */
const resolveMarkerPathBounds = (
    path: ShapedListMarker['paths'][number],
    translateX: number,
    translateY: number,
    rotation: MarkerRotation | null,
): Bounds => {
    return transformBounds(path.bounds, (x, y) => {
        const point = { x: translateX + x, y: translateY - y };
        return rotation
            ? {
                  x: rotation.x - (point.y - rotation.y),
                  y: rotation.y + (point.x - rotation.x),
              }
            : point;
    });
};

/**
 * 计算图形型 marker 在一行缩进区域内的位置。
 *
 * @param line - 当前段落首行指标。
 * @param size - 缩放后的 marker 尺寸。
 * @param input - 当前 Text Render 输入。
 * @param snapshot - 当前排版 snapshot。
 * @returns marker 左上坐标。
 */
const resolveGraphicMarkerPosition = (
    line: TextRenderSnapshot['lineMetrics'][number],
    size: { width: number; height: number },
    input: TextRenderInput,
    snapshot: TextRenderSnapshot,
): { x: number; y: number } => {
    const gap = input.fontSize * MARKER_GAP_EM;
    const { width, height } = size;
    return snapshot.writingMode === 'vertical-rl'
        ? {
              x: line.leftEdgeX + (line.rightEdgeX - line.leftEdgeX - width) / 2,
              y: (snapshot.listMarkerIndent - gap - height) / 2,
          }
        : {
              x: (snapshot.listMarkerIndent - gap - width) / 2,
              y: line.top + (line.bottom - line.top - height) / 2,
          };
};

/**
 * 把预先塑形的 SVG marker 编号转换为最终 path 与可见边界。
 *
 * @param shaped - 正文排版前已经生成的 SVG 编号 path。
 * @param position - marker 左上坐标。
 * @param width - marker 缩放后宽度。
 * @param height - marker 缩放后高度。
 * @returns 可直接绘制的编号 paths 与边界；无需编号或塑形失败时返回空结果。
 */
const buildGraphicMarkerNumberGeometry = (
    shaped: ShapedListMarker | null,
    position: { x: number; y: number },
    width: number,
    height: number,
): {
    paths: Extract<ListMarkerGeometry, { type: 'graphic' }>['items'][number]['numberPaths'];
    bounds: Bounds | null;
} => {
    if (!shaped) return { paths: [], bounds: null };
    const yBounds = collectMarkerYBounds(shaped.paths);
    const centerY = yBounds ? (yBounds.minY + yBounds.maxY) / 2 : shaped.baseline / 2;
    const baseline = position.y + height / 2 + centerY;
    const translateX = position.x + width / 2 - shaped.width / 2;
    let bounds: Bounds | null = null;
    const paths = shaped.paths.map((path) => {
        const pathTranslateX = translateX + path.x;
        const pathTranslateY = path.y + baseline - shaped.baseline;
        bounds = unionBounds(bounds, resolveMarkerPathBounds(path, pathTranslateX, pathTranslateY, null));
        return {
            d: path.d,
            transform: `translate(${pathTranslateX} ${pathTranslateY}) scale(1 -1)`,
        };
    });
    return { paths, bounds };
};

/**
 * 为字符型 marker 补齐与 snapshot 同轮的 path 变换和可见边界。
 *
 * @param input - 已完成字体解析的当前输入。
 * @param snapshot - 本轮正文排版 snapshot。
 * @param prepared - 正文排版前已塑形的字符 marker 事实。
 * @returns 可供边界与绘制共同消费的字符 marker geometry。
 */
const buildCharacterMarkerGeometry = (
    input: TextRenderInput,
    snapshot: TextRenderSnapshot,
    prepared: Extract<ListMarkerShaping, { type: 'character' }>,
): ListMarkerGeometry => {
    const marker = input.listMarker;
    if (marker?.kind !== 'character') return { type: 'character', items: [], bounds: null };
    const items: Extract<ListMarkerGeometry, { type: 'character' }>['items'][number][] = [];
    let bounds: Bounds | null = null;
    let paragraph = 0;

    snapshot.lineMetrics.forEach((line) => {
        if (line.isSoftWrap) return;
        const shaped = prepared.markers[paragraph] ?? null;
        paragraph += 1;
        if (!shaped) return;
        const gap = input.fontSize * MARKER_GAP_EM;
        const ordered = ORDERED_MARKER_STYLES.has(marker.style);
        const pathBounds = collectMarkerYBounds(shaped.paths);
        const pathHeight = pathBounds ? pathBounds.maxY - pathBounds.minY : input.fontSize;
        const vertical = snapshot.writingMode === 'vertical-rl';

        // 横排有序 marker 右对齐到缩进末端；无序 marker 居中，竖排则对齐当前文字列中心。
        const offsetX = vertical
            ? line.leftEdgeX + (line.rightEdgeX - line.leftEdgeX - shaped.width) / 2
            : ordered
              ? snapshot.listMarkerIndent - gap - shaped.width
              : (snapshot.listMarkerIndent - gap - shaped.width) / 2;
        // 竖排按 path 视觉高度居中；横排空行使用 marker 自身基线，其余行跟随正文基线。
        const targetBaseline = vertical
            ? (snapshot.listMarkerIndent - gap - pathHeight) / 2 + (pathBounds?.maxY ?? shaped.baseline)
            : line.byteStart === line.byteEnd
              ? line.top + shaped.baseline
              : line.baseline;
        // 保留既有朝向白名单：竖排仅 decimal/lower-latin 旋转 90°，其他 marker 样式不改变朝向。
        const rotation =
            vertical && VERTICAL_ROTATED_MARKER_STYLES.has(marker.style)
                ? { x: offsetX + shaped.width / 2, y: (snapshot.listMarkerIndent - gap) / 2 }
                : null;
        const paths = shaped.paths.map((path) => {
            const translateX = path.x + offsetX;
            const translateY = path.y + targetBaseline - shaped.baseline;
            bounds = unionBounds(bounds, resolveMarkerPathBounds(path, translateX, translateY, rotation));
            return {
                d: path.d,
                transform: `translate(${translateX} ${translateY}) scale(1 -1)`,
            };
        });
        items.push({
            paths,
            rotation: rotation ? `rotate(90 ${rotation.x} ${rotation.y})` : null,
        });
    });

    return { type: 'character', items, bounds };
};

/**
 * 为图形型 marker 补齐素材位置、编号 path 与可见边界。
 *
 * @param input - 已完成字体解析的当前输入。
 * @param snapshot - 本轮正文排版 snapshot。
 * @param prepared - 正文排版前已计算的素材尺寸与 SVG 编号 path。
 * @returns 可供边界与绘制共同消费的图形型 marker geometry。
 */
const buildGraphicMarkerGeometry = (
    input: TextRenderInput,
    snapshot: TextRenderSnapshot,
    prepared: Extract<ListMarkerShaping, { type: 'graphic' }>,
): ListMarkerGeometry => {
    if (prepared.width <= 0 || prepared.height <= 0) {
        return { type: 'graphic', width: 0, height: 0, items: [], bounds: null };
    }
    const items: Extract<ListMarkerGeometry, { type: 'graphic' }>['items'][number][] = [];
    let bounds: Bounds | null = null;
    let paragraph = 0;

    snapshot.lineMetrics.forEach((line) => {
        if (line.isSoftWrap) return;
        const position = resolveGraphicMarkerPosition(line, prepared, input, snapshot);
        const number = buildGraphicMarkerNumberGeometry(
            prepared.numbers[paragraph] ?? null,
            position,
            prepared.width,
            prepared.height,
        );
        items.push({ position, numberPaths: number.paths });
        bounds = unionBounds(
            bounds,
            {
                left: position.x,
                top: position.y,
                right: position.x + prepared.width,
                bottom: position.y + prepared.height,
            },
            number.bounds,
        );
        paragraph += 1;
    });

    return {
        type: 'graphic',
        width: prepared.width,
        height: prepared.height,
        items,
        bounds,
    };
};

/**
 * 在 output 创建任何 SVG 节点前补齐当前输入的 marker 定位与边界。
 *
 * @param input - 已完成字体解析的当前输入。
 * @param snapshot - 本轮正文排版 snapshot。
 * @param prepared - 与 snapshot 同轮、已完成 Text Engine 塑形的 marker 事实。
 * @returns 可同时驱动边界和绘制的私有 marker geometry；未声明 marker 时返回 null。
 */
export const buildListMarkerGeometry = (
    input: TextRenderInput,
    snapshot: TextRenderSnapshot,
    prepared: ListMarkerShaping | null,
): ListMarkerGeometry | null => {
    if (!input.listMarker || !prepared) return null;
    if (input.listMarker.kind === 'character') {
        return prepared.type === 'character' ? buildCharacterMarkerGeometry(input, snapshot, prepared) : null;
    }
    return prepared.type === 'graphic' ? buildGraphicMarkerGeometry(input, snapshot, prepared) : null;
};
